import { useEffect, useRef, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { pdaZoneOfflineService } from '@/offline/pda-zone.service';
import { networkService } from '@/services/network.service';
import { toast } from '@/utils/toast';

interface ZoneSyncPayload {
  zoneSetId: string;
  incidentId?: string;
  tenantId: string;
}

/**
 * Listens for zone sync push messages (from service worker or Capacitor)
 * and caches the zone set and its zones for offline use.
 *
 * Mount this hook once at the app root level.
 */
export function useZoneSyncListener() {
  const processingRef = useRef<Set<string>>(new Set());

  const handleZoneSync = useCallback(async (payload: ZoneSyncPayload) => {
    const { zoneSetId, tenantId, incidentId } = payload;
    if (!zoneSetId || !tenantId) return;

    // Deduplicate concurrent syncs for same zone set
    if (processingRef.current.has(zoneSetId)) return;
    processingRef.current.add(zoneSetId);

    try {
      if (!networkService.isOnline()) {
        console.log('[ZoneSyncListener] Offline, deferring zone sync for:', zoneSetId);
        return;
      }

      toast.info('Downloading zone data for offline use...');

      const result = await pdaZoneOfflineService.syncZoneSetForOffline(
        tenantId,
        zoneSetId,
        incidentId
      );

      if (result.zones > 0) {
        toast.success(`Downloaded ${result.zones} zones for offline use`);
      }
    } catch (err) {
      console.error('[ZoneSyncListener] Zone sync failed:', err);
      toast.error('Failed to download zone data');
    } finally {
      processingRef.current.delete(zoneSetId);
    }
  }, []);

  useEffect(() => {
    // Listen for messages from service worker (web platform)
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ZONE_SYNC_REQUESTED') {
        handleZoneSync(event.data.payload);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    // Listen for native push notifications (Capacitor platform)
    let nativeListenerHandle: { remove: () => Promise<void> } | null = null;
    if (Capacitor.isNativePlatform()) {
      import('@capacitor/push-notifications').then(({ PushNotifications }) => {
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          if (notification.data?.syncAction === 'zone_sync') {
            handleZoneSync({
              zoneSetId: notification.data.zoneSetId,
              incidentId: notification.data.incidentId,
              tenantId: notification.data.tenantId,
            });
          }
        }).then(handle => {
          nativeListenerHandle = handle;
        });
      });
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
      if (nativeListenerHandle) {
        nativeListenerHandle.remove();
      }
    };
  }, [handleZoneSync]);
}
